import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material'
import { useEffect, useRef } from 'react'

import { StoreFragment } from '@/graphql'
import useT from '@/i18n/useT'
import { LocationHelpers } from '@/utils/location.helpers'

import {
  StoreFormContent,
  StoreFormProps,
  useStoreForm,
} from './AddEditStoreShared'

type StoreFormDialogProps = StoreFormProps & {
  open: boolean
  saving: boolean
  onClose: (store?: StoreFragment | null) => void
}

const AddEditStoreDialog = ({
  open,
  onClose,
  onSubmit,
  ...props
}: StoreFormDialogProps) => {
  const { t } = useT()
  const didSetValues = useRef(false)

  const handleClose = (store?: StoreFragment | null) => {
    onClose(store)
    formik.resetForm()
  }

  const { saving } = props

  const { formik, needsToEdit, onPlaceSelected } = useStoreForm({
    ...props,
    onSubmit: props => onSubmit(props).then(() => handleClose()),
    enableReinitialize: true,
  })

  useEffect(() => {
    if (props.store && props.mode === 'edit' && !didSetValues.current) {
      formik.setValues({
        name: props.store.location?.name || '',
        address: props.store.location
          ? LocationHelpers.toString(props.store.location)
          : '',
        isActive: !props.store.inactiveAt,
      })
      didSetValues.current = true
    }
  }, [props.store, props.mode, formik])

  return (
    <Dialog open={open} onClose={() => handleClose()} fullWidth>
      <form noValidate onSubmit={formik.handleSubmit}>
        <DialogTitle>
          {props.mode === 'add'
            ? t('addEditStore.dialog.addStore')
            : t('addEditStore.dialog.editStore')}
        </DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <StoreFormContent
            mode={props.mode}
            formik={formik}
            needsToEdit={needsToEdit}
            onPlaceSelected={onPlaceSelected}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleClose()} disabled={formik.isSubmitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            loading={saving || formik.isSubmitting}
            loadingPosition="end"
            disabled={saving || needsToEdit}
          >
            {props.mode === 'add'
              ? t('addEditStore.dialog.addStore')
              : t('common.saveChanges')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

export default AddEditStoreDialog
